import { createFileRoute, Link } from "@tanstack/react-router";
import { ClipboardList, PenLine, Flame, ArrowRight, Sparkles, ShieldAlert } from "lucide-react";
import { CaseStudyHub } from "@/components/site/CaseStudyHub";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const CASE_STUDY_FAQS = [
  {
    q: "Can a 214(b) refusal really be reversed for a fresher?",
    a: "Yes - even without prior work experience, reversals happen every intake. What matters is diagnosing why the officer doubted your academic continuity or funding and fixing that exact point on your DS-160 and in your pitch.",
  },
  {
    q: "How soon can I reattempt after a 214(b)?",
    a: "Legally, immediately. Practically, wait until you've fixed at least one material thing (DS-160, funding structure, or answer delivery) and can explain the change in 20 seconds. That usually takes 2-4 weeks.",
  },
  {
    q: "Does an 8-month gap without work experience always mean refusal?",
    a: "No. Post-graduation gaps are refused when they look aimless. When you demonstrate structured prerequisite skill preparation, targeted technical certifications, and clear academic progression, the concern is defused.",
  },
  {
    q: "Is SELF/Family funding a red flag without a salary history?",
    a: "It becomes a red flag when liquid funds look sudden or unexplained. Linking parental savings, clean bank statements, and family financial stability directly to your study plan resolves fund origin doubts.",
  },
  {
    q: "Do I need to switch universities for a reattempt?",
    a: "Only if the university itself was the weak link. Most reversals keep the same I-20 and overhaul the DS-160 narrative, gap explanation, and interview delivery.",
  },
  {
    q: "What is different about WeLivdIt's Refusal Support?",
    a: "5 escalating mocks that stress-test the exact weak spot, DS-160 line-by-line review, and a red-flag fix list built from 300+ prior refusal cases. Priced in INR.",
  },
  {
    q: "How long is the Refusal Support program?",
    a: "About 2-3 weeks of intensive prep depending on your interview date. We work to your consulate slot.",
  },
];

export const Route = createFileRoute("/refusal-case-study")({
  head: () => ({
    meta: [
      { title: "F1 Visa Approved After 214(b) Rejection: Northeastern University (NEU) | WeLivdIt" },
      {
        name: "description",
        content:
          "Delhi denial to Mumbai approval — same profile, restructured answers. Read the full NEU MSCS F-1 visa interview transcript, officer pushback, and WeLivdIt specialist breakdown.",
      },
      { property: "og:title", content: "F1 Visa Approved After 214(b) Rejection: Northeastern University (NEU)" },
      {
        property: "og:description",
        content:
          "Delhi denial to Mumbai approval — same profile, restructured answers under WeLivdIt mentorship. Full transcript + verified WhatsApp screenshots.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://welivdit.com/refusal-case-study" },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://welivdit.com/refusal-case-study" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "F1 Visa Approved After 214(b) Rejection: Northeastern University (NEU)",
          description:
            "Delhi denial to Mumbai approval — same profile, restructured answers under WeLivdIt mentorship. Full F-1 visa interview transcript and specialist breakdown.",
          author: { "@type": "Organization", name: "WeLivdIt" },
          publisher: { "@type": "Organization", name: "WeLivdIt", url: "https://welivdit.com" },
          mainEntityOfPage: "https://welivdit.com/refusal-case-study",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: CASE_STUDY_FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://welivdit.com/" },
            {
              "@type": "ListItem",
              position: 2,
              name: "F1 Visa Approved After 214(b) Rejection (NEU Case Study)",
              item: "https://welivdit.com/refusal-case-study",
            },
          ],
        }),
      },
    ],
  }),
  component: RefusalVictoriesPage,
});

function RefusalVictoriesPage() {
  return (
    <div className="flex flex-col bg-background text-foreground relative">
      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-border/60">
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-primary/8 via-transparent to-amber-500/8" />
          <div className="relative mx-auto max-w-6xl px-5 py-16 sm:py-20 lg:py-24">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
              <Sparkles className="h-3.5 w-3.5" /> Refusal Recovery Program
            </span>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl">
              Refusal to Approval: <span className="text-primary">Our Comeback Stories</span>
            </h1>
            <p className="mt-4 text-lg font-medium text-foreground/85 max-w-3xl">
              Most recent 214(b) refusal cases turn out to be approved on their next attempt.
            </p>
            <p className="mt-3 text-lg text-muted-foreground max-w-2xl leading-relaxed">
              A 214(b) visa rejection is devastating, but it is not the end of your US dream. Behind every approval on
              this page was a student who got a piece of paper that said &quot;no&quot; - and turned it around with a
              sharper story, a cleaner DS-160, and hours of hard practice.
            </p>

            {/* DIRECT JUMP CTA GROUP */}
            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5">
              <Button asChild size="lg" className="gap-2 font-semibold">
                <Link to="/refusal-support">
                  Book Refusal Support <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="gap-2 bg-card/80 backdrop-blur-md">
                <a href="#case-study">
                  <ShieldAlert className="h-4 w-4 text-amber-600" />
                  Skip to Case Study ↓
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Multi-profile verified case study hub */}
        <CaseStudyHub />


        {/* Process */}
        <section className="border-b border-border/60">
          <div className="mx-auto max-w-6xl px-5 py-16">
            <h2 className="font-display text-3xl font-bold tracking-tight text-center">
              Our Proprietary Refusal Recovery Process
            </h2>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {[
                {
                  n: "01",
                  icon: ClipboardList,
                  title: "The DS-160 & Profile Autopsy",
                  body: "Line-by-line audit of your previous DS-160 and profile - every inconsistency an officer could have flagged in 30 seconds is surfaced and fixed.",
                },
                {
                  n: "02",
                  icon: PenLine,
                  title: "The Narrative Rewrite",
                  body: "We rebuild your 30-second story from scratch - university choice, funding, family ties, and post-study intent - so every answer reinforces the same clean thread.",
                },
                {
                  n: "03",
                  icon: Flame,
                  title: "High-Stress Consulate Simulations",
                  body: "Aggressive back-to-back mocks that mimic hostile officer body language, rapid-fire follow-ups, and 15-second answer windows.",
                },
              ].map((s) => (
                <div key={s.n} className="rounded-3xl border border-border bg-card p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <s.icon className="h-6 w-6 text-primary" />
                    <span className="text-xs font-mono text-muted-foreground">{s.n}</span>
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison */}
        <section className="border-b border-border/60 bg-muted/20">
          <div className="mx-auto max-w-5xl px-5 py-16">
            <h2 className="font-display text-3xl font-bold tracking-tight text-center">
              Standard Prep vs. Refusal Support
            </h2>
            <div className="mt-8 grid gap-5 md:grid-cols-2">
              <div className="rounded-3xl border border-border bg-background p-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Standard Prep</p>
                <h3 className="mt-2 font-display text-xl font-semibold">First-timer Coaching</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Great for first-timers, standard mock formats, general strategy. Solid rehearsal when your profile is
                  already clean and your story is clear.
                </p>
              </div>
              <div className="relative rounded-3xl border-2 border-primary bg-primary/5 p-6 shadow-lg shadow-primary/10">
                <span className="absolute -top-3 left-6 rounded-full bg-primary px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
                  For refusals
                </span>
                <p className="text-xs font-semibold uppercase tracking-wider text-primary">Refusal Support</p>
                <h3 className="mt-2 font-display text-xl font-semibold">Deep-Dive Recovery</h3>
                <p className="mt-2 text-sm text-foreground leading-relaxed">
                  Deep-dive DS-160 autopsy, complete narrative rewrite, aggressive high-stress simulation, and dedicated
                  psychological confidence rebuilding for your re-attempt.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="border-b border-border/60 bg-muted/20">
          <div className="mx-auto max-w-3xl px-5 py-16">
            <h2 className="font-display text-3xl font-bold tracking-tight text-center">Refusal FAQ</h2>
            <Accordion type="single" collapsible className="mt-8">
              <AccordionItem value="q1">
                <AccordionTrigger>Will my previous rejection automatically ruin my next attempt?</AccordionTrigger>
                <AccordionContent>
                  No. Officers can see your refusal history, but a refusal is not a lifetime ban. What they care about
                  is what has <em>changed</em> - your story, your DS-160, your answers, and your composure. Fix those,
                  and you walk in as a fresh case.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="q2">
                <AccordionTrigger>Should I change my university for the second attempt?</AccordionTrigger>
                <AccordionContent>
                  Only if the change is genuine and defensible. Switching universities to &quot;look different&quot;
                  without a real reason is a bigger red flag than sticking with your original choice and articulating it
                  well.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="q3">
                <AccordionTrigger>Do I need to fill out a new DS-160?</AccordionTrigger>
                <AccordionContent>
                  Yes - every visa appointment needs a fresh DS-160 confirmation. This is a chance to fix
                  inconsistencies from your first attempt and tighten every field so it lines up perfectly with your new
                  story.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        {/* MAIN FAQ ACCORDIONS */}
        <section className="border-t border-border/60">
          <div className="mx-auto max-w-3xl px-5 py-16">
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
              Frequently asked questions
            </h2>
            <Accordion type="single" collapsible className="mt-6">
              {CASE_STUDY_FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`f${i}`}>
                  <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-foreground/75 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Final CTA */}
        <section>
          <div className="mx-auto max-w-3xl px-5 py-20 text-center">
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
              Don&apos;t leave your second attempt to chance.
            </h2>
            <p className="mt-4 text-muted-foreground">
              Rebuild your DS-160 narrative and stress-test your answers before stepping up to the window.
            </p>
            <div className="mt-8">
              <Button asChild size="lg" className="gap-2 text-base">
                <Link to="/refusal-support">
                  Schedule Your Refusal Recovery Session Now <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* STICKY FLOATING CTA BAR */}
      <div className="sticky bottom-0 left-0 right-0 z-30 bg-background/90 backdrop-blur-md border-t border-border p-3 sm:px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3 text-xs">
          <div className="hidden sm:flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-amber-600" />
            <span className="font-medium text-foreground">
              Got a 214(b) refusal slip? Get a forensic profile audit.
            </span>
          </div>
          <Button asChild size="sm" className="w-full sm:w-auto gap-1.5 text-xs font-semibold ml-auto">
            <Link to="/refusal-support">
              Book Refusal Audit <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
